import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe,
} from '@stripe/react-stripe-js';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppConfiguration } from 'config';
import {
  Card, CardBody, Col, Container, Form, FormFeedback, FormGroup, Input, Label, Row,
} from 'reactstrap';
import { cancelCardUpdate, completeCardUpdate_2 } from 'store/payment_methods/actions';
import { useFormik } from 'formik';
import { us_states } from 'common/data/us_states';
import * as Yup from 'yup';
import * as CONSTANTS from 'common/utils/data_validations';
import { MetaTags } from 'react-meta-tags';
import { Link } from 'react-router-dom';
import { add_funds_options_billing, animatedlogo } from 'helpers/sf_constants';
import { SfDollarStringFormatter, getPrimaryButtonClass, getPrimaryTextClass } from 'helpers/sf_utils';

function BillingInformation(props) {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardUpdateSuccessful, setCardUpdateSuccessful] = useState(false);

  const CREDIT_AMOUNT_OPTION = {
    label: '$250',
    value: 250,
  };

  const [selectedCreditAmount, setSelectedCreditAmount] = useState(CREDIT_AMOUNT_OPTION);


  const { payment_methods, loading, user, updating_card } = useSelector(
    (state) => ({
      payment_methods: state.PaymentMethods.payment_methods,
      loading: state.PaymentMethods.loading,
      user: state.Account.user,
      updating_card: state.PaymentMethods.updating_card
    }),
    shallowEqual,
  );

  const confirmStripeIntent = async () => {
    const { errorPaymentMethod, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardNumberElement),
      billing_details: {
        address: {
          city: JSON.parse(user).data.city,
          country: 'US',
          line1: JSON.parse(user).data.address,
          line2: null,
          postal_code: JSON.parse(user).data.zip_code,
          state: JSON.parse(user).data.state
        },
        name: JSON.parse(user).first_name + ' ' + JSON.parse(user).last_name,
      },
    });
    const { error } = await stripe.confirmCardSetup(props?.clientSecret, {
      payment_method: paymentMethod.id,
      return_url: `${rool_url}/login`,
    });

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      dispatch(cancelCardUpdate());
      setErrorMessage(error.message);
      setIsSubmitting(false);
    } else {
      setIsSubmitting(false);
      setCardUpdateSuccessful(true);
      dispatch(completeCardUpdate_2(
        {paymentMethod: paymentMethod, 
         user_id: JSON.parse(user).data.id,
         initial_deposit: selectedCreditAmount.value
          }));
      // Your customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }
  };
  const rool_url = AppConfiguration.STRIPE_REDIRECT_ROOT;
  const handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    setIsSubmitting(true);
    setCardUpdateSuccessful(false);

    confirmStripeIntent();
  };

  const state_names = [];
  us_states.map((e) => {
    state_names.push({ label: e.isocode, value: e.name });
  });
  const optionGroupStates = [
    {
      label: 'Select State',
      options: state_names,
    },
  ];

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      first_name: props?.firstname,
      last_name: props?.lastname,
      address: props?.address,
      city: props?.city,
      state: optionGroupStates[0].options.find((opt) => (opt.label === props?.state)),
      zip_code: props?.zipcode,
      company_name: props?.companyname,
      card_expiry: '',
      promo_code: ''

    },
    validationSchema: Yup.object({
      first_name: Yup.string()
        .required(CONSTANTS.STR_REQUIRED_FIRSTNAME)
        .min(2),
      last_name: Yup.string().required(CONSTANTS.STR_REQUIRED_LASTNAME).min(2),
      address: Yup.string().required(CONSTANTS.STR_REQUIRED_ADDRESS).min(3),
      city: Yup.string().required(CONSTANTS.STR_REQUIRED_CITY).min(2),
      state: Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      }),
      zip_code: Yup.string()
        .required(CONSTANTS.STR_REQUIRED_ZIP)
        .matches(CONSTANTS.zipcodeRegExp, 'Please enter a valid zip code'),
      company_name: Yup.string()
        .required(CONSTANTS.STR_REQUIRED_FIRSTNAME)
        .min(2),
    }),
    onSubmit: (values) => {
      const submitValues = JSON.parse(JSON.stringify(values));
      submitValues.state = values.state.label; // For Select Fields
      submitValues.timezone = values.timezone.value; // For Select Fields
    },
  });
  return (
    <React.Fragment>

      <MetaTags>
        <title>Register Now | {AppConfiguration.WEBSITE}</title>
      </MetaTags>
        <div className="account-pages my-1 pt-sm-5">
            <Container>
                <Row className="justify-content-center">
                    <Col md={8} lg={6} xl={6}>
                        <Card className="overflow-hidden">
                            <div className="bg-white bg-soft">
                                <Row>
                                    <Col xs={12}>
                                    <div className="text-center p-4">
                                        <img src={animatedlogo} />
                                    </div>
                                    <div className="text-dark px-4">
                                        <h5 className="text-dark b">Create an account</h5>
                                        <p>
                                        Create your {AppConfiguration.WEBSITE} Ad Center account below or
                                        if you need assistance contact us at {AppConfiguration.WHITE_LABELING_CONTACT_PHONE}
                                        </p>
                                    </div>
                                    </Col>
                                </Row>
                            </div>
                            <CardBody className="pt-0">
                                <Form
                                className="form-horizontal"
                                onSubmit={async (e) => {
                                    e.preventDefault();
                                    handleSubmit(e)
                                    //validation.handleSubmit();
                                    // props?.onCloseClick();
                                    return false;
                                }}  
                                >
                                <div className="">
                                {/* Billing information starts */}
                                <div className="mx-2 mt-2 mb-2">
                                    <Label className={`label-title font-weight-semibold ${getPrimaryTextClass()}`}>
                                    Billing Information
                                    </Label>
                                </div>
                                <div className="mx-2">
                                    <Row>
                                    <Col lg="6">
                                        <FormGroup className="mb-0">
                                        <Label className="font-weight-semibold">
                                            Credit Card
                                        </Label>
                                        <CardNumberElement
                                            className="form-control"
                                            placeholder="Enter card number"
                                        />
                                        </FormGroup>
                                    </Col>
                                    <Col lg="3">
                                        <FormGroup className="mb-0">
                                        <Label className="font-weight-semibold">
                                            Expiry date
                                        </Label>
                                        <CardExpiryElement
                                            className="form-control"
                                            name="card_expiry"

                                        />
                                        </FormGroup>
                                    </Col>
                                    <Col lg="3">
                                        <FormGroup className="mb-0">
                                        <Label className="font-weight-semibold">
                                            CVV Code
                                        </Label>
                                        <CardCvcElement
                                            className="form-control"
                                        />
                                        </FormGroup>
                                    </Col>
                                    </Row>
                                </div>
                                {/* Billing Information ends */}
                                {/* Billing Address starts */}
                                <div className="mx-2 mt-3 mb-2">
                                    <Row>
                                    <Col lg="6">
                                        <FormGroup className="mb-0">
                                            <Label className="font-weight-semibold">
                                                Initial Deposit Amount
                                            </Label>
                                            {/* <Select
                                                value={selectedStatusGroup}
                                                onChange={setSelectedStatusGroup}
                                                options={optionGroupStatus}
                                                classNamePrefix="select2-selection"
                                            /> */}
                                            <div className="styled-select select">
                                                <select
                                                value={selectedCreditAmount.value}
                                                onChange={(selectedCreditOption) => {
                                                    setSelectedCreditAmount({
                                                    label: 'Label',
                                                    value: selectedCreditOption.target.value,
                                                    });
                                                }}
                                                >
                                                {add_funds_options_billing.map((e) => (
                                                    <option
                                                    key={e.value}
                                                    value={e.value}
                                                    >
                                                    {SfDollarStringFormatter(e.label)}
                                                    </option>
                                                ))}
                                                </select>
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col lg="6">
                                        <FormGroup className="mb-0">
                                        <Label className="font-weight-semibold">
                                            Promo Code
                                        </Label>
                                        <Input
                                            type="text"
                                            name="promo_code"
                                            className="form-control"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            defaultValue={validation.values.promo_code || ''}
                                            invalid={
                                        !!(validation.touched.last_name
                                        && validation.errors.last_name)
                                        }
                                        />
                                        {validation.touched.last_name
                                    && validation.errors.last_name ? (
                                        <FormFeedback type="invalid">
                                        {validation.errors.last_name}
                                        </FormFeedback>
                                            ) : null}
                                        </FormGroup>
                                    </Col>
                                    <Col>
                                        <div className="form-check mt-2">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="customControlInline"
                                                name="terms"
                                            />
                                            <label
                                            className="form-check-label"
                                            htmlFor="customControlInline"
                                            >
                                            I have read and agreed to the <span className='text-sfblue'>Terms and Conditions</span>
                                            </label>
                                        </div>
                                    </Col>
                                    <Col lg="12">
                                        <div className="mt-4 mb-4 d-grid">
                                            <button
                                                className={`btn ${getPrimaryButtonClass()} btn-block`}
                                                type="submit"
                                                >
                                                {(updating_card || isSubmitting) ? 
                                                'Please wait...' : 'Create an account'}
                                            </button>
                                        </div>
                                    </Col>
                                </Row>
                                </div>
                                </div>
                                </Form>
                            </CardBody>
                        </Card>
                        <div className="mt-5 text-center">
                            <p>
                            Already have an account ?{" "}
                            <Link to="/login" className="font-weight-medium text-sfblue">
                                {" "}
                                Login
                            </Link>{" "}
                            </p>
                            <p>© {new Date().getFullYear()} {AppConfiguration.WEBSITE}</p>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    </React.Fragment>
  );
}
BillingInformation.propTypes = {
  options: PropTypes.object,
  clientSecret: PropTypes.string,
  onCancel: PropTypes.func,
};

export default BillingInformation;
